import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Truck, RefreshCw, Shield, Star, Minus, Plus } from 'lucide-react';
import Header from './components/Header4';
import Footer from './components/Footer4';
import Button from './components/Button';
import ProductCard from './components/ProductCard';

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedSize, setSelectedSize] = useState('M');
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);
    const [isWishlisted, setIsWishlisted] = useState(false);

    const product = {
        id: id,
        name: 'Banarasi Silk Saree',
        price: 12999,
        originalPrice: 15999,
        description: 'This exquisite Banarasi silk saree features intricate zari work with traditional motifs. Handwoven by master artisans from Varanasi, this saree is perfect for weddings and special occasions. The rich silk fabric drapes beautifully and the gold zari adds a regal touch.',
        images: [
            'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=800',
        ],
        sizes: ['XS', 'S', 'M', 'L', 'XL'],
        rating: 4.8,
        reviews: 124,
        fabric: '100% Pure Silk',
        origin: 'Varanasi, India',
    };

    const relatedProducts = [
        { id: 2, name: 'Designer Lehenga Set', price: 24500, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Cotton Kurta Collection', price: 3499, image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400', tag: 'NEW' },
        { id: 4, name: 'Chanderi Silk Dupatta', price: 4999, image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Embroidered Anarkali', price: 8999, originalPrice: 11999, image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    return (
        <div className="w-full min-h-screen bg-white" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <Header />
            <link href="https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <main className="pt-20 md:pt-24">
                {/* Breadcrumb */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center gap-2 text-xs text-[#6B7280]">
                        <button onClick={() => navigate('/template4')} className="hover:text-[#1B4965] transition-colors bg-transparent border-none cursor-pointer p-0">Home</button>
                        <span>/</span>
                        <button onClick={() => navigate('/template4/shop')} className="hover:text-[#1B4965] transition-colors bg-transparent border-none cursor-pointer p-0">Shop</button>
                        <span>/</span>
                        <span className="text-[#1B4965] font-medium">{product.name}</span>
                    </div>
                </div>

                {/* Product Detail */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                        {/* Images */}
                        <div>
                            <div className="aspect-[3/4] overflow-hidden rounded-sm mb-3 bg-[#F0F7FA]">
                                <img
                                    src={product.images[selectedImage]}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                                {product.images.map((img, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedImage(index)}
                                        className={`aspect-square overflow-hidden rounded-sm border-2 transition-all ${
                                            selectedImage === index ? 'border-[#1B4965]' : 'border-transparent'
                                        }`}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Info */}
                        <div className="flex flex-col">
                            <h1 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-2">{product.name}</h1>

                            {/* Rating */}
                            <div className="flex items-center gap-2 mb-4">
                                <div className="flex gap-0.5">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'fill-[#62B6CB] text-[#62B6CB]' : 'text-gray-200'}`} />
                                    ))}
                                </div>
                                <span className="text-sm text-[#6B7280]">{product.rating} ({product.reviews} reviews)</span>
                            </div>

                            {/* Price */}
                            <div className="flex items-center gap-3 mb-6">
                                <span className="text-2xl md:text-3xl font-bold text-[#1B4965]">{'\u20B9'}{product.price.toLocaleString()}</span>
                                {product.originalPrice && (
                                    <>
                                        <span className="text-lg text-[#6B7280] line-through">{'\u20B9'}{product.originalPrice.toLocaleString()}</span>
                                        <span className="text-sm font-semibold text-[#62B6CB]">
                                            {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
                                        </span>
                                    </>
                                )}
                            </div>

                            <p className="text-sm text-[#6B7280] leading-relaxed mb-6">{product.description}</p>

                            {/* Details */}
                            <div className="flex gap-6 mb-6 text-sm">
                                <div>
                                    <span className="text-[#6B7280]">Fabric:</span>
                                    <span className="ml-2 text-[#1A1A1A] font-medium">{product.fabric}</span>
                                </div>
                                <div>
                                    <span className="text-[#6B7280]">Origin:</span>
                                    <span className="ml-2 text-[#1A1A1A] font-medium">{product.origin}</span>
                                </div>
                            </div>

                            {/* Size Selector */}
                            <div className="mb-6">
                                <p className="text-sm font-medium text-[#1A1A1A] mb-3">Size</p>
                                <div className="flex gap-2">
                                    {product.sizes.map((size) => (
                                        <button
                                            key={size}
                                            onClick={() => setSelectedSize(size)}
                                            className={`w-10 h-10 text-sm font-medium rounded-sm border transition-all ${
                                                selectedSize === size
                                                    ? 'bg-[#1B4965] text-white border-[#1B4965]'
                                                    : 'bg-white text-[#1A1A1A] border-[#E5E7EB] hover:border-[#1B4965]'
                                            }`}
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Quantity */}
                            <div className="mb-6">
                                <p className="text-sm font-medium text-[#1A1A1A] mb-3">Quantity</p>
                                <div className="inline-flex items-center border border-[#E5E7EB] rounded-sm">
                                    <button
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        className="w-10 h-10 flex items-center justify-center text-[#6B7280] hover:text-[#1B4965] transition-colors"
                                    >
                                        <Minus className="w-4 h-4" />
                                    </button>
                                    <span className="w-12 h-10 flex items-center justify-center text-sm font-medium text-[#1A1A1A] border-x border-[#E5E7EB]">{quantity}</span>
                                    <button
                                        onClick={() => setQuantity(quantity + 1)}
                                        className="w-10 h-10 flex items-center justify-center text-[#6B7280] hover:text-[#1B4965] transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 mb-8">
                                <Button variant="primary" size="lg" className="flex-1 gap-2">
                                    <ShoppingCart className="w-4 h-4" /> Add to Cart
                                </Button>
                                <button
                                    onClick={() => setIsWishlisted(!isWishlisted)}
                                    className={`w-12 h-12 flex items-center justify-center rounded-sm border transition-all ${
                                        isWishlisted ? 'bg-[#1B4965] border-[#1B4965]' : 'border-[#E5E7EB] hover:border-[#1B4965]'
                                    }`}
                                >
                                    <Heart className="w-5 h-5" style={{ color: isWishlisted ? '#FFFFFF' : '#1B4965', fill: isWishlisted ? '#FFFFFF' : 'none' }} />
                                </button>
                            </div>

                            {/* Trust Badges */}
                            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-[#E5E7EB]">
                                <div className="flex flex-col items-center gap-2 text-center">
                                    <Truck className="w-5 h-5 text-[#1B4965]" />
                                    <span className="text-xs text-[#6B7280]">Free Shipping</span>
                                </div>
                                <div className="flex flex-col items-center gap-2 text-center">
                                    <RefreshCw className="w-5 h-5 text-[#1B4965]" />
                                    <span className="text-xs text-[#6B7280]">Easy Returns</span>
                                </div>
                                <div className="flex flex-col items-center gap-2 text-center">
                                    <Shield className="w-5 h-5 text-[#1B4965]" />
                                    <span className="text-xs text-[#6B7280]">Secure Payment</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Related Products */}
                    <div className="mt-16 md:mt-24">
                        <h2 className="text-xl md:text-2xl font-semibold text-[#1B4965] mb-8 text-center">You May Also Like</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                            {relatedProducts.map((product) => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default ProductDetail;
